import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { addDays, differenceInDays, getDate, getDay, getISOWeek, getMonth, getYear } from 'date-fns';
import { format } from 'date-fns';
import { BehaviorSubject, of, Subject } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';

import { ProjectDetailTooltipComponent } from './project-detail-tooltip/project-detail-tooltip.component';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { ActionsService } from '@core/actions-modals/actions.service';
import { FiltersStoreService } from '@core/filters-store.service';
import { convertTokens, legacyParse } from '@date-fns/upgrade/v2';
import { AuthService } from '@features/auth/auth.service';
import { StatusesColors } from '@features/projects/statuses-colors';
import { StatusesTitles } from '@features/projects/statuses-titles';
import { SchedulerService } from '@features/scheduler/scheduler.service';
import { FilterTypeFields } from '@features/scheduler/scheduler-container/filters/filterTypes';
import { EntityEditService } from '@services/common/entity-edit.service';
import { HolidaysService } from '@services/common/holidays.service';
import { MenuService } from '@services/common/menu.service';
import { TimeService } from '@services/common/time.service';
import { GenerateMonthService } from '@shared/components/scheduler-calendar/services/generate-month.service';
import { ProjectDetailService } from '@shared/components/scheduler-calendar/services/project-detail.service';
import { UserPermissions } from '@shared/enums/permissions';
import { ProjectStatusesEnum } from '@shared/enums/project-statuses.enum';
import { FilterStorageModel } from '@shared/interfaces/filtersStore.model';
import { ToasterService } from '@ui-kit/app-toast/toaster.service';
import { CrudService } from 'collection';
import { Collection, CollectionModel, ItemModel } from 'collection';

@Component({
  selector: 'scheduler-calendar',
  templateUrl: './scheduler-calendar.component.html',
  styleUrls: ['./scheduler-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SchedulerCalendarComponent implements OnInit, OnDestroy {
  @Input() hideActionBtns = false;
  @Input() canProjectBeCreatedOnCellClick = true;
  @Output() cdkList = new EventEmitter();
  @ViewChild('calendarContainer') calendarContainer: ElementRef;
  @ViewChild('confirmInput', { static: true }) confirmInput: TemplateRef<HTMLElement>;
  @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;
  @ViewChild('menu') menu: MatMenu;
  @Input() set filters(filters) {
    if (filters) {
      this.schedulerFilters = filters;
    }
  }

  strategy$ = of('immediate');
  breakpointState: boolean;
  hoveredDay: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  clickedDay: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  cdkDragList = ['unscheduled-projects'];
  @HostListener('window:resize', ['$event'])
  onResize(e) {
    this.breakpointState = e.currentTarget.innerWidth <= 1400;
    if (this.collection.loaded) {
      this.calculateDayCellWidth();
    }
  }

  schedulerFilters: FilterStorageModel;
  collection: CollectionModel;
  unscheduledProjectsCollection: CollectionModel;
  projectItem: ItemModel;
  updateProjectItem: ItemModel;
  holidaysObj: { [key: string]: boolean } = {};
  filterTypesEnum = FilterTypeFields;
  year: any;
  calendar: any = [];
  daysObject: any = {};
  daysObject2: any = {};
  dayCellWidth = new BehaviorSubject(0);
  maxProgressesLength = 2;
  today: string;
  currentYear: number;
  progressesPeriodDates: { from: string; to: string };
  dropPoint: { x: string; y: string };
  dragData: { [key: string]: any };
  dropDate: string;
  isProgressHovered = true;
  isWorker: boolean;

  private destroyed$ = new Subject();

  assignUserCollection;
  constructor(
    private _auth: AuthService,
    private _menuService: MenuService,
    private _schedulerService: SchedulerService,
    private _timeService: TimeService,
    private _filtersStore: FiltersStoreService,
    private _toastService: ToasterService,
    private _actionsService: ActionsService,
    private _cdRef: ChangeDetectorRef,
    private _projectDetailService: ProjectDetailService,
    private _entityService: EntityEditService,
    private _generateMonthService: GenerateMonthService,
    private _crud: CrudService,
    private _holidaysService: HolidaysService,
    private _breakpointObserver: BreakpointObserver
  ) {
    this.isWorker = this._auth.isWorker();
    this.projectItem = this._projectDetailService.projectItem;
    this.updateProjectItem = this._projectDetailService.updateProjectItem;
    this.assignUserCollection = this._projectDetailService.assignUserCollection;
    this.schedulerFilters = this._filtersStore.filters.projectSchedulerPlanner;
    this.unscheduledProjectsCollection = this._schedulerService.unscheduledProjectsCollection;
    this.collection = new Collection({
      api: this._crud.createEntity({ name: 'project' }),
      params: {
        expand: 'client,display_status,parent_project',
        per_page: 30
      }
    });

    this._breakpointObserver.observe(['(max-width: 1400px)']).subscribe((breakpoint: BreakpointState) => {
      this.breakpointState = breakpoint.matches;
    });

    effect(() => {
      this.calculateMenuShift(!this._menuService.isMinimizedEnabled());
      this.calculateDayCellWidth();
    });
  }

  openTooltip(elem: ProjectDetailTooltipComponent) {
    elem.menuTrigger.openMenu();
    elem.open();
  }

  getStatusTooltipValue(item: string): string {
    return StatusesTitles[item];
  }

  getStatusColor(item: string): string {
    switch (item) {
      case ProjectStatusesEnum.InProgress:
        return StatusesColors.InProgress;
      case ProjectStatusesEnum.Finished:
        return StatusesColors.Finished;
      case ProjectStatusesEnum.BehindSchedule:
        return StatusesColors.BehindSchedule;
      case ProjectStatusesEnum.OverBudget:
        return StatusesColors.OverBudget;
    }
  }

  drop<T>(event: CdkDragDrop<T>): void {
    this.dropDate = event.container.id.slice(4);
    this.dragData = {
      ...event.item.data,
      end_date: this.dropDate,
      start_date: this.dropDate
    };
    this.dropPoint = {
      x: event.dropPoint.x + 'px',
      y: event.dropPoint.y + 'px'
    };
    this._cdRef.detectChanges();
    this.menuTrigger.menuData = { item: { content: this.menu } };
    this.menuTrigger.openMenu();
  }

  onScheduleModalSave(e?): void {
    if (this.dragData?.title) {
      this.handleProjectCreating(e);
    } else {
      this.saveChangesToProject();
    }
  }

  private handleProjectCreating(e?): void {
    this._entityService
      .openProjectModal({ isSubproject: e, preSelectedData: this.dragData })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ confirmed }) => {
        if (confirmed) {
          this.reloadCollection();
          this._schedulerService.reloadCollection$.next(true);
        }
      });
    this.dragData = null;
    this.dropPoint = null;
    this.dropDate = null;
  }

  private saveChangesToProject() {
    const requestData = {
      data: {
        end_date: this.dragData.end_date,
        start_date: this.dragData.start_date
      }
    };

    this.updateProjectItem
      .setRouteParams('id', this.dragData.id)
      .updateItem(requestData)
      .subscribe(() => {
        this.menuTrigger.closeMenu();
        this.unscheduledProjectsCollection.reload().subscribe();
        this.collection.reload().subscribe((projects) => {
          this.fillProgressesObj(this.daysObject);
          this.fillProgressesObj(this.daysObject2);

          this._schedulerService.reloadCollection$.next(true);
        });
        this.updateProjectItem.clear();
      });
  }

  private fillProgressesObj(obj): void {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        obj[key].progresses = [];
      }
    }
  }

  closeMenu() {
    this.menuTrigger.closeMenu();
  }

  initCalendar(year: number, month, date) {
    this.calendar = [];
    this.calendar.push({
      date: new Date(year, month, date),
      days: this.generateCalendar(month, year)
    });

    const lastWeekDays = this.calendar[0].days[this.calendar[0].days.length - 1];
    this.progressesPeriodDates = {
      from: this.calendar[0].days[0][0].date,
      to: lastWeekDays[lastWeekDays.length - 1].date
    };
  }

  generateCalendar(month, year) {
    const weeksInMonth = this.countMonthWeeks(month, year);

    return this.generateMonths(weeksInMonth, month, year);
  }

  generateMonths(weeksInMonth, month, year) {
    const monthParams = { weeksInMonth, month, year };

    return this._generateMonthService.generateMonths(monthParams, this);
  }

  countMonthWeeks(month, year) {
    const startDay = new Date(year, month - 1, 1).getDay();
    const daysMonth = new Date(year, month, 0).getDate();
    const endDay = new Date(year, month - 1, daysMonth).getDay();

    if (startDay === 0) {
      const weeksCount = (daysMonth + startDay + (6 - endDay)) / 7;
      return weeksCount + 1;
    }

    return (daysMonth + startDay + (6 - endDay)) / 7;
  }

  onProgressClick(e) {
    e.stopPropagation();
    this.clickedDay.next(null);
  }

  onProgressHover(project?) {
    if (project) {
      this.setHoverWithProgressState(project);
    } else {
      this.disableHover();
    }
  }

  setHoverOnCell(day): void {
    this.hoveredDay.next(day);
    if (this.clickedDay.getValue() !== day.date && !this.isProgressHovered) {
      this.clickedDay.next(null);
    }
  }

  private setHoverWithProgressState(day): void {
    this.isProgressHovered = true;
    this.hoveredDay.next(day);
  }

  disableHover() {
    this.hoveredDay.next(null);
    this.isProgressHovered = false;
  }

  private generateCdkDragList(): void {
    let keysOfDays = Object.keys(this.daysObject);
    keysOfDays = keysOfDays.map((item) => 'day-' + item);
    this.cdkDragList = ['unscheduled-projects'];
    this.cdkDragList = this.cdkDragList.concat(keysOfDays);
    this.cdkList.next(this.cdkDragList);
  }

  private initCalendarPeriod(from): void {
    const month = getMonth(legacyParse(from)) + 1;
    const year = getYear(legacyParse(from));
    const date = getDate(legacyParse(from));

    this.initCalendar(year, month, date);

    this.generateCdkDragList();
  }

  onDayClick(event: MouseEvent, day): void {
    const canCreateProject = this._auth.can(UserPermissions.createProjects);
    if (!this.isProgressHovered && this.canProjectBeCreatedOnCellClick && canCreateProject) {
      this.clickedDay.next(day.date);

      const { date } = day;
      this.menuTrigger.menuData = { item: { content: this.menu } };
      this.dropPoint = {
        x: event.x + 'px',
        y: event.y + 'px'
      };
      this.dropDate = date;
      this.dragData = {
        end_date: date,
        start_date: date,
        title: 'Create project'
      };
      this.menuTrigger.openMenu();
    }
  }

  isHoliday(date): boolean {
    return this.holidaysObj?.hasOwnProperty(date);
  }

  private pushToPeriod(date, progress, dateObj): void | boolean {
    const dateFormatted = this._timeService.getDateFormat(date, 'YYYY-MM-DD');
    if (!dateObj[dateFormatted]) {
      return false;
    }

    dateObj[dateFormatted].progresses.push(progress);
  }

  private completeFullIndexes(progress): void {
    let startDate = format(legacyParse(addDays(legacyParse(progress.a_start), 1)), convertTokens('YYYY-MM-DD'));

    for (let i = 0; i < progress.a_length - 1; i++) {
      this.pushToPeriod(
        startDate,
        {
          ...progress,
          a_start: startDate,
          a_end: startDate,
          a_length: 1,
          is_empty: true
        },
        this.daysObject2
      );

      startDate = format(legacyParse(addDays(legacyParse(startDate), 1)), convertTokens('YYYY-MM-DD'));
    }
  }

  private generateWeeksProgresses(countWeeks, startDate, project): void {
    let startWeekDate = this._timeService.getDateFormat(startDate, 'YYYY-MM-DD');

    for (let i = 0; i < countWeeks; i++) {
      const lastWeekDate = format(legacyParse(addDays(legacyParse(startWeekDate), 6)), convertTokens('YYYY-MM-DD'));
      this.pushToPeriod(
        startWeekDate,
        {
          ...project,
          has_prev: true,
          has_next: true,
          a_start: startWeekDate,
          a_end: lastWeekDate,
          a_length: 7
        },
        this.daysObject
      );

      startWeekDate = this._timeService.getDateFormat(addDays(legacyParse(lastWeekDate), 1), 'YYYY-MM-DD');
    }
  }

  private generateProgresses(project): void | boolean {
    // @ts-ignore
    const projectStartPeriod = project.start_date;
    // @ts-ignore
    const projectEndPeriod = project.end_date;

    const firstWeek = getISOWeek(legacyParse(projectStartPeriod));
    const lastWeek = getISOWeek(legacyParse(projectEndPeriod));

    if (firstWeek === lastWeek) {
      this.pushToPeriod(
        projectStartPeriod,
        {
          ...project,
          a_start: projectStartPeriod,
          a_end: projectEndPeriod,
          a_length: differenceInDays(legacyParse(projectEndPeriod), legacyParse(projectStartPeriod)) + 1
        },
        this.daysObject
      );
    } else {
      const dayOfWeek = getDay(legacyParse(projectStartPeriod));

      let lastDateOfFirstProgress = addDays(legacyParse(projectStartPeriod), 7 - dayOfWeek);

      if (dayOfWeek === 0) {
        lastDateOfFirstProgress = projectStartPeriod;
      }

      const dayOfWeek2 = getDay(legacyParse(projectEndPeriod));

      let lastDateOfFirstProgress2 = addDays(legacyParse(projectEndPeriod), -dayOfWeek2 + 1);

      if (dayOfWeek2 === 1) {
        lastDateOfFirstProgress2 = projectEndPeriod;
      } else if (dayOfWeek2 === 0) {
        lastDateOfFirstProgress2 = addDays(legacyParse(projectEndPeriod), -6);
      }

      this.pushToPeriod(
        projectStartPeriod,
        {
          ...project,
          has_next: true,
          a_start: projectStartPeriod,
          a_end: format(legacyParse(lastDateOfFirstProgress), convertTokens('YYYY-MM-DD')),
          a_length:
            differenceInDays(
              legacyParse(format(legacyParse(lastDateOfFirstProgress), convertTokens('YYYY-MM-DD'))),
              legacyParse(projectStartPeriod)
            ) + 1
        },
        this.daysObject
      );

      this.pushToPeriod(
        format(legacyParse(lastDateOfFirstProgress2), convertTokens('YYYY-MM-DD')),
        {
          ...project,
          has_prev: true,
          a_start: format(legacyParse(lastDateOfFirstProgress2), convertTokens('YYYY-MM-DD')),
          a_end: projectEndPeriod,
          a_length:
            differenceInDays(
              legacyParse(projectEndPeriod),
              legacyParse(format(legacyParse(lastDateOfFirstProgress2), convertTokens('YYYY-MM-DD')))
            ) + 1
        },
        this.daysObject
      );

      let countWeeks = lastWeek - firstWeek;

      if (firstWeek > lastWeek) {
        countWeeks = lastWeek;
      }

      if (countWeeks >= 2) {
        const startNextWeekDate = addDays(legacyParse(lastDateOfFirstProgress), 1);
        this.generateWeeksProgresses(countWeeks - 1, startNextWeekDate, project);
      }
    }
  }

  maxProgressesLengthChanged(progressesLength, week, day) {
    day['showAll'] = true;
    day['showMore'] = 0;
    week[0]['maxProgressesLength'] = progressesLength;
  }

  private lowestUnusedNumber(progresses) {
    const sequence = progresses.map((item) => item.index);

    const arr = sequence.slice(0);
    arr.sort((a, b) => a - b);

    return arr.reduce((lowest, num, i) => {
      const seqIndex = i + 0;
      return num !== seqIndex && seqIndex < lowest ? seqIndex : lowest;
    }, arr.length + 0);
  }

  calculateDayCellWidth(): void {
    setTimeout(() => {
      const element = this.calendarContainer.nativeElement;
      const dayCellWidth = element.querySelector('.day')?.offsetWidth;
      this.dayCellWidth.next(dayCellWidth);
    }, 500);
  }

  private generateDayProgresses(): void {
    for (const [key] of Object.entries(this.daysObject)) {
      if (this.daysObject[key].progresses.length) {
        const progresses = this.daysObject[key].progresses;

        const existProgresses = this.daysObject2[key].progresses;
        let index = 0;

        if (existProgresses.length) {
          index = this.lowestUnusedNumber(existProgresses);
        }

        progresses.forEach((progress) => {
          progress.index = index;
          this.pushToPeriod(key, progress, this.daysObject2);
          this.completeFullIndexes(progress);

          if (existProgresses.length) {
            index = this.lowestUnusedNumber(existProgresses);
          }
        });
      }
    }
  }

  onDeleteAction(project): void {
    const title = { titleMain: marker('Are you sure you want to delete this project'), titleVal: ` ${project.name}?` };

    const modalConfig = {
      title: title,
      description: marker(
        'All project information including logged hours, checklists, deviations, and documents will be deleted.'
      ),
      confirmButtonText: marker('Yes, I confirm'),
      template: this.confirmInput,
      checkboxName: 'deleteProject',
      checkboxes: {
        deleteProject: {
          state: false,
          label: marker('I understand that this action cannot be undone and all project info will be deleted')
        }
      }
    };

    this._actionsService
      .confirm(modalConfig)
      .pipe(take(1))
      .subscribe(({ confirmed, modal }) => {
        if (confirmed && modalConfig.checkboxes.deleteProject.state) {
          this.projectItem.deleteItem().subscribe(
            (result) => {
              modal.close();
              this.reloadCollection();
              this._toastService.show({
                type: 'success',
                message: marker('Project is successfully deleted')
              });
            },
            ({ error }) => {
              const errorMessage = error.data.message;
              modal.close();
              this._toastService.show({
                type: 'error',
                message: errorMessage
              });
            }
          );
        }
      });
  }

  toggleCheckbox(e, config, key) {
    e.preventDefault();
    e.stopPropagation();
    config.checkboxes[key].state = !config.checkboxes[key].state;
  }

  onEditAction(project): void {
    const preSelectedUsersIds = this.assignUserCollection.items.map((item) => item.id);
    const preSelectedData = { assigned_users: preSelectedUsersIds };
    const isSubproject = project.hasOwnProperty('parent_id') && project.parent_id;

    this._entityService
      .openProjectModal({ id: project.id, isSubproject, preSelectedData })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ confirmed }) => {
        if (confirmed) {
          this.reloadCollection();
          this._schedulerService.reloadCollection$.next(true);
        }
      });
  }

  private reloadCollection() {
    this.daysObject = {};
    this.daysObject2 = {};

    const currentYearInFilters = getYear(legacyParse(this.schedulerFilters.get(this.filterTypesEnum.FromDate)));

    // there is an issue on displaying holidays on months those related to 2 different years
    // possible solution is to send holidays fro 3 years diapason
    if (this.currentYear !== currentYearInFilters || !Object.getOwnPropertyNames(this.holidaysObj).length) {
      this.currentYear = currentYearInFilters;
      this.initHolidays();
    }

    this.initCalendarPeriod(this.schedulerFilters.get(this.filterTypesEnum.FromDate));
    this.setProjectListParam(this.filterTypesEnum.Status, 'status');
    this.setProjectListParam(this.filterTypesEnum.P_Client, 'client_id[]');
    this.collection
      .setParams('start_date', this.progressesPeriodDates.from)
      .setParams('end_date', this.progressesPeriodDates.to)
      .search();
  }

  private handleCollectionReload(projects?) {
    this.calculateDayCellWidth();
    const projectsToHandle = projects ? projects : this.collection.items;
    projectsToHandle.forEach((project: any) => {
      // @ts-ignore
      if (project.start_date && project.end_date) {
        this.generateProgresses(project);
      }
    });

    this.generateDayProgresses();
  }

  private calculateSidebarShift(isSidebarOpen: boolean): void {
    const divideBy = 7;
    // additional sidebar size in px
    const sidebarSizeInPx = 178;
    const additionalValue = !isSidebarOpen ? sidebarSizeInPx / divideBy : -(sidebarSizeInPx / divideBy);

    this.dayCellWidth.next(this.dayCellWidth.getValue() + additionalValue);
  }

  private calculateMenuShift(isMenuOpen: boolean): void {
    const divideBy = 7;
    // menu size 200px - 50px of collapsed menu
    const menuSizeInPx = 150;
    const additionalValue = isMenuOpen ? menuSizeInPx / divideBy : -(menuSizeInPx / divideBy);

    this.dayCellWidth.next(this.dayCellWidth.getValue() + additionalValue);
  }

  private initFilterSubscription() {
    this.schedulerFilters.changed
      .pipe(
        takeUntil(this.destroyed$),
        tap((params) => {
          if (params?.fromTime && params?.toTime) {
            this.initHolidays();
          }
        }),
        filter((params) => !!Object.keys(params || {}).length)
      )
      .subscribe((changedFilters) => {
        if (changedFilters && changedFilters.field !== 'unscheduled') {
          this.reloadCollection();
        }

        this.calculateDayCellWidth();
      });
  }

  private setProjectListParam(filterKey: string, paramKey: string) {
    const ids = this.schedulerFilters.get(filterKey);

    if (filterKey === this.filterTypesEnum.Status) {
      ids && ids.length
        ? this.collection.setParams(this.filterTypesEnum.DisplayStatus, ids)
        : this.collection.clearParams(this.filterTypesEnum.DisplayStatus);
    } else {
      ids && ids.length ? this.collection.setParams(paramKey, ids.join(' ,')) : this.collection.clearParams(paramKey);
    }
  }

  initHolidays(): void {
    const from = this.schedulerFilters.get(this.filterTypesEnum.FromDate);
    const to = this.schedulerFilters.get(this.filterTypesEnum.ToDate);

    this._holidaysService
      .getHolidays({ from, to })
      .pipe(
        take(1),
        map((list) => (this.holidaysObj = list)),
        map(() => this._cdRef.markForCheck())
      )
      .subscribe();
  }

  initProjectModalSubscription(): void {
    this._schedulerService.reloadCollection$
      .pipe(
        takeUntil(this.destroyed$),
        filter((reloaded) => !!reloaded),
        tap(() => this.reloadCollection())
      )
      .subscribe();
  }
  ngOnInit() {
    this.reloadCollection();

    this.initFilterSubscription();

    this.collection.onLoad$.pipe(takeUntil(this.destroyed$)).subscribe((projects) => {
      this.handleCollectionReload(projects.items);
    });

    this._schedulerService.$isOpenSidebar.pipe(takeUntil(this.destroyed$)).subscribe((isSidebarOpen: boolean) => {
      this.calculateSidebarShift(isSidebarOpen);
      this.calculateDayCellWidth();
    });

    this.initProjectModalSubscription();
  }

  ngOnDestroy(): void {
    this.collection.clear();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
